/**
 * The organization claim workflow (GH-120, SPEC.md §6.3, DEC-005).
 *
 * The single rule this file exists to enforce: **code can create a claim; only a recorded human
 * decision can approve one.** An approved claim is what lets an organization publish events and turn on
 * a payment rail, so an automatic approval anywhere in this codebase would be the platform
 * vouching for an organization nobody verified.
 *
 * That rule is expressed three ways, deliberately redundantly, because one of them will
 * eventually be edited by someone who does not know why it is there:
 *
 *   1. `approved` is unreachable from `submitted` — a reviewer must move it through `reviewing`.
 *   2. `unmetApprovalRequirements` names what a reviewer must have collected. **It is not yet
 *      called on the approval path** — as of 2026-08-20 nothing outside this package's tests uses
 *      it, so treat it as the specification of the checklist, not as an enforced gate. The one
 *      requirement that *is* enforced server-side is the IRS registry listing, in
 *      `services/api/src/staff-routes.ts` (`verifyIrsListing`), on both routes that reach
 *      `approved`. Wiring the remaining five is open work; this comment previously claimed the
 *      wiring existed, which made the gap invisible.
 *   3. The database records every transition in an append-only decision log with a rationale.
 */
import { StateMachine } from "./state-machine.js";
export const ORGANIZATION_CLAIM_STATES = [
    "draft",
    "submitted",
    "reviewing",
    "more_information",
    "approved",
    "rejected",
    "withdrawn",
];
export const organizationClaimMachine = new StateMachine({
    name: "organization_claim",
    initial: "draft",
    transitions: {
        draft: ["submitted", "withdrawn"],
        // Note what is missing: `submitted -> approved`. A claim nobody opened cannot be approved,
        // so the reviewing step cannot be skipped by a caller passing the end state directly.
        submitted: ["reviewing", "withdrawn"],
        reviewing: ["more_information", "approved", "rejected"],
        more_information: ["reviewing", "withdrawn", "rejected"],
        // Approval is not terminal: SPEC.md §6.4 requires emergency suspension to remain possible,
        // and a renewal review can send an organization back for more information.
        approved: ["more_information"],
        rejected: [],
        withdrawn: [],
    },
    progression: ["draft", "submitted", "reviewing", "approved"],
    terminal: ["rejected", "withdrawn"],
});
const REQUIREMENT_LABELS = {
    legalName: "legal name",
    ein: "EIN",
    formationEvidence: "formation or governing evidence",
    address: "address",
    determinationLetter: "IRS determination letter",
    irsRegistryListing: "IRS registry listing (Pub 78 / BMF)",
    authorityDocumentation: "authority documentation",
    settlementAccountVerified: "settlement account ownership",
};
/**
 * Returns the human-readable names of everything still missing. Empty means approvable.
 *
 * Returns a list rather than a boolean so a reviewer is told what is outstanding instead of being
 * refused without explanation — a reviewer who cannot see why will approve around the check.
 */
export function unmetApprovalRequirements(evidence) {
    return Object.keys(REQUIREMENT_LABELS)
        .filter((key) => !evidence[key])
        .map((key) => REQUIREMENT_LABELS[key]);
}
/**
 * Whether reaching this state should flip the organization's `claim_status` to `approved`.
 *
 * A single named predicate, so the one place that grants an organization the ability to take
 * money is greppable.
 */
export function grantsDonationCapability(state) {
    return state === "approved";
}
/**
 * What a donor is told about an organization's standing.
 *
 * Every non-approved state collapses to the same sentence on purpose. "Under review" would tell a
 * donor that a claim exists, which is information about the organization's private dealings with
 * the platform, and it invites them to wait rather than give elsewhere.
 */
export function donorVisibleVerificationLabel(claimStatus, paymentsEnabled) {
    if (claimStatus === "approved" && paymentsEnabled)
        return "Verified organization";
    if (claimStatus === "suspended")
        return "Temporarily unavailable";
    return "Unclaimed — donations disabled";
}
//# sourceMappingURL=organization-claim.js.map